import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { 
  ACHIEVEMENT_DEFINITIONS, 
  PERMANENT_ACHIEVEMENTS, 
  CYCLE_MILESTONES,
  AchievementDefinition 
} from '@/components/membership/achievements/achievementDefinitions';
import { toast } from 'sonner';

interface AchievementOverride {
  id: string;
  achievement_type: string;
  badge?: string | null;
  name?: string | null;
  description?: string | null;
  requirement?: string | null;
  points?: number | null;
  is_active: boolean;
  updated_by?: string | null;
  created_at: string;
  updated_at: string;
}

export interface MergedAchievement extends AchievementDefinition {
  isOverridden: boolean;
  isActive: boolean;
  originalPoints: number;
}

export const useSystemAchievementOverrides = () => {
  const [overrides, setOverrides] = useState<AchievementOverride[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchOverrides = useCallback(async () => {
    try {
      const { data, error: fetchError } = await supabase
        .from('system_achievement_overrides')
        .select('*');

      if (fetchError) throw fetchError;
      setOverrides(data || []);
    } catch (err) {
      console.error('Error fetching overrides:', err);
      setError(err instanceof Error ? err.message : 'Error fetching overrides');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOverrides();
  }, [fetchOverrides]);

  // Create a merged version of achievements with overrides applied
  const getMergedAchievements = useCallback((): MergedAchievement[] => {
    return ACHIEVEMENT_DEFINITIONS.map(def => {
      const override = overrides.find(o => o.achievement_type === def.type);
      
      if (override) {
        return {
          ...def,
          badge: override.badge || def.badge,
          name: override.name || def.name,
          description: override.description || def.description,
          requirement: override.requirement || def.requirement,
          points: override.points ?? def.points,
          isOverridden: true,
          isActive: override.is_active,
          originalPoints: def.points,
        };
      }
      
      return {
        ...def,
        isOverridden: false,
        isActive: true,
        originalPoints: def.points,
      };
    });
  }, [overrides]);

  const getMergedPermanentAchievements = useCallback((): MergedAchievement[] => {
    return PERMANENT_ACHIEVEMENTS.map(def => {
      const override = overrides.find(o => o.achievement_type === def.type);
      
      if (override) {
        return {
          ...def,
          badge: override.badge || def.badge,
          name: override.name || def.name,
          description: override.description || def.description,
          requirement: override.requirement || def.requirement,
          points: override.points ?? def.points,
          isOverridden: true,
          isActive: override.is_active,
          originalPoints: def.points,
        };
      }
      
      return {
        ...def,
        isOverridden: false,
        isActive: true,
        originalPoints: def.points,
      };
    });
  }, [overrides]);

  const getMergedCycleMilestones = useCallback((): MergedAchievement[] => {
    return CYCLE_MILESTONES.map(def => {
      const override = overrides.find(o => o.achievement_type === def.type);
      
      if (override) {
        return {
          ...def,
          badge: override.badge || def.badge,
          name: override.name || def.name,
          description: override.description || def.description,
          requirement: override.requirement || def.requirement,
          points: override.points ?? def.points,
          isOverridden: true,
          isActive: override.is_active,
          originalPoints: def.points,
        };
      }
      
      return {
        ...def,
        isOverridden: false,
        isActive: true,
        originalPoints: def.points,
      };
    });
  }, [overrides]);

  // Get the effective points for an achievement type
  const getAchievementPoints = useCallback((type: string): number => {
    const override = overrides.find(o => o.achievement_type === type);
    if (override && override.points !== null) {
      return override.points;
    }
    const def = ACHIEVEMENT_DEFINITIONS.find(a => a.type === type);
    return def?.points || 0;
  }, [overrides]);

  // Get merged achievement by type
  const getMergedAchievementByType = useCallback((type: string): MergedAchievement | undefined => {
    return getMergedAchievements().find(a => a.type === type);
  }, [getMergedAchievements]);

  // Check if an achievement is active
  const isAchievementActive = useCallback((type: string): boolean => {
    const override = overrides.find(o => o.achievement_type === type);
    return override?.is_active ?? true;
  }, [overrides]);

  // Update or create an override
  const updateOverride = async (
    type: string, 
    changes: Partial<Pick<AchievementOverride, 'badge' | 'name' | 'description' | 'requirement' | 'points' | 'is_active'>>
  ) => {
    try {
      const { data: userData } = await supabase.auth.getUser();
      
      const { error: upsertError } = await supabase
        .from('system_achievement_overrides')
        .upsert({
          achievement_type: type,
          ...changes,
          updated_by: userData.user?.id,
        }, {
          onConflict: 'achievement_type',
        });

      if (upsertError) throw upsertError;

      toast.success('Logro actualizado correctamente');
      await fetchOverrides();
      return true;
    } catch (err) {
      console.error('Error updating override:', err);
      toast.error('Error al actualizar logro');
      return false;
    }
  };

  // Delete an override (restore to default)
  const deleteOverride = async (type: string) => {
    try {
      const { error: deleteError } = await supabase
        .from('system_achievement_overrides')
        .delete()
        .eq('achievement_type', type);

      if (deleteError) throw deleteError;

      toast.success('Logro restaurado a valores por defecto');
      await fetchOverrides();
      return true;
    } catch (err) {
      console.error('Error deleting override:', err);
      toast.error('Error al restaurar logro');
      return false;
    }
  };

  // Calculate total points for a list of achievement types
  const calculateTotalPoints = useCallback((types: string[]): number => {
    return types.reduce((sum, type) => {
      if (!isAchievementActive(type)) return sum;
      return sum + getAchievementPoints(type);
    }, 0);
  }, [getAchievementPoints, isAchievementActive]);

  return {
    overrides,
    loading,
    error,
    getMergedAchievements,
    getMergedPermanentAchievements,
    getMergedCycleMilestones,
    getMergedAchievementByType,
    getAchievementPoints,
    isAchievementActive,
    updateOverride,
    deleteOverride,
    calculateTotalPoints,
    refetch: fetchOverrides,
  };
};
